import moment from "moment";
import { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DATE_FORMATS } from "../../../constants";
import { RillContext } from "../../../rill/RillReact/share/base";
import { isFemaleCycleSectionEnabledFromConfig } from "../../../utils/client-configuration-helpers";
import { buildPrExerciseKeysByDay } from "../../../utils/workout-stats";
import useScrollspyStickyHeader from "../../../hooks/useScrollspyStickyHeader";
import CalendarCategoryFilterBar from "./components/CalendarCategoryFilterBar";
import CalendarMonthHeader from "./components/CalendarMonthHeader";
import {
  categoryKeysWithPeriod,
  LOG_CATEGORY_KEYS,
  logCategoryLabel,
} from "./components/calendarFilterCategories";
import CalendarCollapseToggle from "./components/CalendarCollapseToggle";
import LogWeekRow, { LogDayHeaderLabel } from "./components/LogWeekRow";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

const weeksInMonth = [...Array(6)].map((_, i) => i);
const daysInWeek = [...Array(7)].map((_, i) => i);

const LogView = () => {
  const { locals } = useContext(RillContext);
  const periodEnabled = isFemaleCycleSectionEnabledFromConfig(
    locals.clientConfiguration
  );
  const categoryKeys = useMemo(
    () => categoryKeysWithPeriod(LOG_CATEGORY_KEYS, periodEnabled),
    [periodEnabled]
  );
  const [activeFilters, setActiveFilters] = useState(() => [
    ...categoryKeysWithPeriod(LOG_CATEGORY_KEYS, periodEnabled),
  ]);
  const [collapsed, setCollapsed] = useState(false);
  const toggleCollapsed = useCallback(() => setCollapsed((c) => !c), []);

  const handleFilterToggle = useCallback((key) => {
    setActiveFilters((current) =>
      current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key]
    );
  }, []);

  const handleToggleAll = useCallback(() => {
    setActiveFilters([...categoryKeys]);
  }, [categoryKeys]);

  const handleClear = useCallback(() => {
    setActiveFilters([]);
  }, []);

  const {
    activeMonth,
    query,
    startOfFirstWeek,
    athlete,
    pathname,
    logExercises = {},
    logFoodLogs = {},
    logNutritionSummary = {},
    logNutritionPlans = [],
    logHabitLogs = {},
    logDailyStats = {},
    logBodyMeasurements = {},
    logProgressPhotos = {},
    logMealPhotos = {},
    logFemaleCycleDays = [],
    dayPersonalRecords = {},
  } = locals;

  const prExerciseKeysByDay = useMemo(
    () => buildPrExerciseKeysByDay(dayPersonalRecords),
    [dayPersonalRecords]
  );
  const repMaxPR = dayPersonalRecords?.repMaxPR;

  const units = locals.activeUser?.units || "metric";

  // Pulled out of the render loop (was inline in weeksInMonth.map below) so
  // rowCount — how many weeks actually render this month, always 4-6, never
  // a fixed 6 — is known before wiring the scrollspy hook, and so the
  // contiguous 0..rowCount-1 row index (not the raw 0-5 weekIndex, which
  // can skip 5) lines up with what registerRow expects.
  const visibleWeeks = useMemo(() => {
    return weeksInMonth
      .map((weekIndex) => {
        const weekStart = startOfFirstWeek.clone().add(weekIndex, "weeks");
        if (weekIndex === 5 && weekStart.month() !== activeMonth.month()) {
          return null;
        }
        const weekDays = daysInWeek.map((dayIndex) => {
          const day = weekStart.clone().add(dayIndex, "days");
          const dayKey = day.format(DATE_DAY_FORMAT);
          return {
            day,
            dayKey,
            isCurrentMonth: day.month() === activeMonth.month(),
            isToday:
              day.format("YYYY-MM-DD") === moment.utc().format("YYYY-MM-DD"),
            exercises: logExercises[dayKey] || [],
            foodLogs: logFoodLogs[dayKey] || [],
            nutritionSummary: (logNutritionSummary[dayKey] || [])[0] || null,
            nutritionPlans: logNutritionPlans,
            habitLogs: (logHabitLogs[dayKey] || [])[0] || null,
            dailyStat: (logDailyStats[dayKey] || [])[0] || null,
            bodyMeasurements: (logBodyMeasurements[dayKey] || [])[0] || null,
            progressPhotos: (logProgressPhotos[dayKey] || [])[0] || null,
            mealPhotos: (logMealPhotos[dayKey] || [])[0] || null,
            prExerciseKeys: prExerciseKeysByDay[dayKey] || new Set(),
          };
        });
        return { weekIndex, weekDays };
      })
      .filter(Boolean);
  }, [
    startOfFirstWeek,
    activeMonth,
    logExercises,
    logFoodLogs,
    logNutritionSummary,
    logNutritionPlans,
    logHabitLogs,
    logDailyStats,
    logBodyMeasurements,
    logProgressPhotos,
    logMealPhotos,
    prExerciseKeysByDay,
  ]);

  // Sticky day-header (scrollspy) — see useScrollspyStickyHeader.js for the
  // full "why" (root: .calendar-month-wrapper's own bounded internal
  // scroll, not the page). Works the same collapsed or not: collapsed mode
  // only changes .log-week-row's vertical layout (log-week-row--collapsed
  // drops the subgrid row-count style and hides empty day-slots) — .day-header
  // is still each column's first child either way, and the row's own
  // `align-items: start` keeps it at the same height across all 7 columns
  // regardless. Column widths are untouched too: .log-day-column's own
  // sizing rules aren't scoped to :not(.log-week-row--collapsed).
  const calendarMonthWrapperRef = useRef(null);
  const { registerRow, stickyHeadRef, activeIndex, colWidths, colCardWidths, containerRect, onScrollSync } =
    useScrollspyStickyHeader({
      rootRef: calendarMonthWrapperRef,
      rowCount: visibleWeeks.length,
      // Measure the whole card (.log-day-column), not just the inner
      // .day-header — .day-header's own rect excludes the card's padding
      // AND margin, so using it directly as a clone column's width (while
      // the clone ALSO applies matching padding/margin on top) double-counts
      // that space and renders the clone narrower than the real row.
      getHeaderCells: (row) => Array.from(row.querySelectorAll(".log-day-column")),
      deps: [collapsed, visibleWeeks],
    });
  const activeWeekDays = activeIndex != null ? visibleWeeks[activeIndex]?.weekDays : null;

  // Navigating to a different month keeps this component mounted (just new
  // `locals`), so without this the wrapper's old scrollTop carries over —
  // e.g. land mid-scroll on week 3 of the new month, with the scrollspy
  // showing a stale header until the user scrolls again. Reset it whenever
  // the visible month actually changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeMonth is a re-run trigger, not a value read in the closure
  useLayoutEffect(() => {
    if (calendarMonthWrapperRef.current) {
      calendarMonthWrapperRef.current.scrollTop = 0;
    }
  }, [activeMonth]);

  return (
    <main className="Calendar LogView">
      <CalendarMonthHeader
        activeMonth={activeMonth}
        athlete={athlete}
        pathname={pathname}
        query={query}
        locals={locals}
        viewOptions={[]}
      />

      <div className="flex justify-between items-center">
        <CalendarCategoryFilterBar
          activeFilters={activeFilters}
          onToggle={handleFilterToggle}
          onToggleAll={handleToggleAll}
          onClear={handleClear}
          categoryKeys={LOG_CATEGORY_KEYS}
          labelForKey={logCategoryLabel}
          periodEnabled={periodEnabled}
        />
        <CalendarCollapseToggle
          collapsed={collapsed}
          onToggle={toggleCollapsed}
        />
      </div>

      {activeWeekDays && (
        <div
          className="log-sticky-head-fixed"
          style={{ top: containerRect.top, left: containerRect.left, width: containerRect.width }}
        >
          <div
            ref={stickyHeadRef}
            className="log-sticky-head-inner"
            style={{ gridTemplateColumns: colWidths.map((w) => `${w}px`).join(" ") }}
          >
            {activeWeekDays.map((d, i) => (
              <div
                key={d.dayKey}
                className={`log-sticky-head-cell${!d.isCurrentMonth ? " other-month" : ""}`}
                style={{ width: colCardWidths[i] }}
              >
                <div className="day-header">
                  <LogDayHeaderLabel day={d.day} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="calendar-month-wrapper" ref={calendarMonthWrapperRef}>
        <div className="log-view-weeks" style={{ overflowX: "auto" }} onScroll={onScrollSync}>
          {visibleWeeks.map(({ weekIndex, weekDays }, rowIndex) => (
            <LogWeekRow
              key={weekIndex}
              ref={registerRow(rowIndex)}
              collapsed={collapsed}
              weekDays={weekDays}
              activeFilters={activeFilters}
              units={units}
              locals={locals}
              repMaxPR={repMaxPR}
              femaleCycleDays={logFemaleCycleDays}
            />
          ))}
        </div>
      </div>
    </main>
  );
};

export default LogView;
